import { 
  Home, 
  TrendingUp, 
  Activity, 
  Settings,
  ChevronRight,
  Database,
  LineChart,
  Monitor,
  Brain,
  Landmark,
  BarChart3,
  BookOpen,
  Layers,
  DollarSign
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  adminOnly?: boolean;
}

const menuItems: MenuItem[] = [
  { 
    title: "Overview", 
    url: "/app/overview", 
    icon: Home,
    description: "Dashboard geral"
  },
  { 
    title: "Market Explorer", 
    url: "/app/markets", 
    icon: TrendingUp,
    description: "Explorar criptoativos"
  },
  { 
    title: "Chart", 
    url: "/chart", 
    icon: LineChart,
    description: "TradingView Charts"
  },
  {
    title: "DeFi Protocols", 
    url: "/app/defi-protocols", 
    icon: Layers,
    description: "TVL e Fees de protocolos DeFi"
  },
  {
    title: "Stablecoins", 
    url: "/app/stablecoins", 
    icon: DollarSign,
    description: "Supply e dominância de stablecoins"
  },
  {
    title: "AlphaQuant",
    url: "/app/alpha-quant",
    icon: Activity,
    description: "Estratégia OI + Funding",
    adminOnly: true,
  },
  { 
    title: "Alpha On-Chain",
    url: "/app/alpha-onchain", 
    icon: Activity,
    description: "Dados on-chain",
    adminOnly: true,
  },
  { 
    title: "Liquidez Macro", 
    url: "/app/macro-btc", 
    icon: Landmark,
    description: "NFCI, CS Alpha, SSR, M2, Fed, RRP"
  },
  { 
    title: "Market Intelligence", 
    url: "/app/market-monitor", 
    icon: Monitor,
    description: "Derivativos, ETF Flows & Smart Money"
  },
  {
    title: "Macro",
    url: "/app/macro",
    icon: BarChart3,
    description: "CPI e Fed Funds (FRED x Polymarket)",
    adminOnly: true,
  },
  { 
    title: "Admin Coverage", 
    url: "/app/admin/coverage", 
    icon: Database,
    description: "Cobertura de dados",
    adminOnly: true,
  },
  { 
    title: "AI Analyst Admin", 
    url: "/app/admin/ai-analyst", 
    icon: Brain,
    description: "Gerenciar AI Analyst",
    adminOnly: true,
  },
  { 
    title: "Knowledge Base", 
    url: "/app/admin/knowledge", 
    icon: BookOpen,
    description: "Base de conhecimento da IA",
    adminOnly: true,
  },
  { 
    title: "Settings", 
    url: "/app/settings", 
    icon: Settings,
    description: "Configurações"
  },
];

export function AppSidebar() {
  const { state } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;
  const isCollapsed = state === "collapsed";
  const { isAdmin } = useUserRole();

  const visibleItems = menuItems.filter(item => !item.adminOnly || isAdmin);

  const isActive = (path: string) => {
    if (path === "/app/overview") return currentPath === "/app" || currentPath === "/app/overview";
    return currentPath.startsWith(path);
  };

  const getNavClass = (path: string) => {
    const active = isActive(path);
    return active 
      ? "bg-gradient-to-r from-primary/20 to-transparent text-primary font-medium border-l-2 border-primary transition-all duration-200" 
      : "hover:bg-sidebar-accent/40 text-sidebar-foreground hover:text-primary transition-all duration-200";
  };

  const getIconClass = (path: string) => {
    const active = isActive(path);
    return active
      ? "h-4 w-4 text-primary"
      : "h-4 w-4 text-sidebar-foreground/70 group-hover:text-primary transition-colors duration-200";
  };

  return (
    <Sidebar className={isCollapsed ? "w-16" : "w-64"}>
      <SidebarHeader className="border-b border-sidebar-border">
        <div className="flex items-center gap-3 px-3 py-4">
          <div className="h-9 w-9 rounded-lg overflow-hidden flex items-center justify-center">
            <img src="/favicon.png" alt="Cristos AI" className="h-9 w-9 object-contain" />
          </div>
          {!isCollapsed && (
            <div>
              <h2 className="text-lg font-bold text-gradient">Cristos</h2>
              <p className="text-xs text-sidebar-foreground/60">AI Trading Platform</p>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className={isCollapsed ? "sr-only" : "text-sidebar-foreground/50 text-xs uppercase tracking-wider"}>
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={item.url} 
                      className={`group ${getNavClass(item.url)}`}
                    >
                      <item.icon className={getIconClass(item.url)} />
                      {!isCollapsed && (
                        <>
                          <span className="flex-1">{item.title}</span>
                          {isActive(item.url) && (
                            <ChevronRight className="h-4 w-4 opacity-60 text-primary" />
                          )}
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                  {!isCollapsed && isActive(item.url) && (
                    <div className="px-6 pb-1 fade-in-up">
                      <p className="text-xs text-sidebar-foreground/50">
                        {item.description}
                      </p>
                    </div>
                  )}
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
